/**
 * Auth Store (Zustand)
 *
 * httpOnly Cookies (E13) — kein localStorage für Tokens!
 * Auth-State wird über GET /auth/me geprüft.
 */

import { create } from 'zustand';
import { api } from '../services/api';

export interface AuthUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: { id: number; name: string; displayName: string; hierarchyLevel: number };
  permissions: string[];
  /** Dashboard-Variante (Phase 2); fehlt = klassisch */
  dashboardVariante?: 'klassisch' | 'persoenlich';
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password) => {
    const user = await api.auth.login(email, password);
    // Login-Antwort liefert dashboardVariante wie /auth/me (Backend, Phase 2 Kalle)
    set({ user, isAuthenticated: true, isLoading: false });
  },

  logout: async () => {
    try {
      await api.auth.logout();
    } catch {
      // Logout auch bei Netzwerkfehler lokal durchführen
    }
    set({ user: null, isAuthenticated: false, isLoading: false });
  },

  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const user = await api.auth.me();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  hasPermission: (permission) => {
    const { user } = get();
    if (!user) return false;
    if (user.role.name === 'admin') return true;
    return user.permissions.includes(permission);
  },

  isAdmin: () => {
    const { user } = get();
    return user?.role.name === 'admin' || false;
  },
}));
